import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { IconOffer } from '../icons';
import { useAuth } from '../context/AuthContext';
import { db, storage, functions } from '../firebaseConfig';
import { collection, query, where, getDocs, orderBy, Timestamp, doc, getDoc } from 'firebase/firestore';
import { ref, getDownloadURL } from 'firebase/storage';
import { httpsCallable } from 'firebase/functions';
import LoadingSpinner from './LoadingSpinner';
import { useLocation, useNavigate } from 'react-router-dom';
import { useIsReadOnly } from '../hooks/useIsReadOnly';
import { downloadTenantDocumentFile, triggerTenantFileDownload } from '../api/tenantFileApi';
import Button from './common/Button';
import { DataTable, Column } from './common/DataTable';
import { RowActionsMenu, RowActionsMenuItem } from './common/RowActionsMenu';
import EmailStatusChip from './EmailStatusChip';
import { formatDate } from '../utils/formatters';
import { INVOICE_FORM_ROUTE } from '../config/cockpitAccess';

// Anpassung für Angebote: Die Struktur eines Angebots-Dokuments aus Firestore
interface Offer {
    id: string;
    userId: string;
    offerNumber: string;
    offerDate: string | Timestamp;
    buyerName: string;
    buyerEmail?: string;
    totalAmount: number;
    currency: string;
    createdAt: Timestamp;
    pdfPath: string;
    status?: string;
    validUntil?: string | Timestamp;
    emailStatus?: {
        // M10a: 'failed' and 'queued' added to align with deliveries lifecycle
        status: 'sent' | 'delivered' | 'opened' | 'bounced' | 'failed' | 'queued';
        sentAt?: any;
        deliveredAt?: any;
        openedAt?: any;
        recipientEmail?: string;
        bounceReason?: string;
    };
}

// Kleine, reine Icon-Bausteine für die Aktions-Spalte (RowActionsMenuItem erwartet `icon`, M-V4-B).
const IconPdf = (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"/></svg>
);
const IconEmail = (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/></svg>
);
const IconCreateInvoice = (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/></svg>
);

/** Sortier-Hilfswert für Firestore-Datumsfelder (Timestamp/String) → Millisekunden. */
const toSortMillis = (value: unknown): number => {
    if (!value) return 0;
    if (value instanceof Timestamp) return value.toMillis();
    if (typeof (value as { seconds?: number })?.seconds === 'number') {
        return (value as { seconds: number }).seconds * 1000;
    }
    const d = new Date(value as string | number);
    return Number.isNaN(d.getTime()) ? 0 : d.getTime();
};

const OfferArchive: React.FC = () => {
    const { t, i18n } = useTranslation(['offer', 'common']);
    const { currentUser, effectiveScopeUid } = useAuth();
    // M11b-3: advisor (read-only) sieht keine mutierenden Aktionen
    // (Angebot→Rechnung, Resend). Download (Lesevorgang) bleibt erhalten.
    const isReadOnly = useIsReadOnly();
    const [offers, setOffers] = useState<Offer[]>([]);
    const [filteredOffers, setFilteredOffers] = useState<Offer[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [downloading, setDownloading] = useState<string | null>(null);

    // Email-Versand (erneut)
    const [sendEmailFor, setSendEmailFor] = useState<string | null>(null);
    const [emailInput, setEmailInput] = useState<string>('');
    const [emailSendStatus, setEmailSendStatus] = useState<Record<string, 'sending' | 'success' | 'error'>>({});

    // Suchparameter
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [isFiltered, setIsFiltered] = useState<boolean>(false);

    // ── Generationen-Zaehler gegen veraltete Ladeantworten ────────────────────
    //
    // `loadGenerationRef` zaehlt bei jedem Lade-Durchlauf hoch (Muster wie
    // InboxPage.tsx): eine ueberholte Antwort darf weder die Liste noch
    // `loading` schreiben. Das ist hier kein Randfall — der Lade-Effekt feuert
    // mehrfach pro Seitenaufruf, siehe die `tRef`-Begruendung unten.
    //
    // `mutationGenerationRef` deckt ab, was eine reine Anfrage-Generation nicht
    // abdeckt: das optimistische `emailStatus`-Update in `handleSendEmail`
    // aendert die Liste nur lokal. Eine Anfrage, die VOR diesem Update startete
    // und DANACH zurueckkommt, ist die juengste Anfrage, kennt den neuen Status
    // aber nicht — der Versand-Haken wuerde wieder verschwinden.
    //
    // Kunden- und Produktliste haben diesen zweiten Zaehler inzwischen durch
    // ein Mutations-Log ersetzt (Serverantwort = Basis, lokales Delta darueber
    // gelegt). Hier bleibt der Zaehler bewusst stehen: die Begruendung steht in
    // `architecture-list-concurrency-merge.md` §6.3. Kurzfassung — es gibt in
    // diesem Archiv weder einen Anlege- noch einen Loeschpfad, die einzige
    // Mutation setzt eine bereits gerenderte Zeile voraus. Eine verworfene
    // Antwort kann die Liste hier also nie leeren, sondern nur ihre
    // Aktualisierung um eine Ladung verzoegern. Der Umbau waere zugleich der
    // teuerste der vier Listen (zwei Listen-States, URL-`?id=`-Zweig,
    // Filter-Zustand) — groesste Flaeche, kleinster Nutzen.
    const loadGenerationRef = useRef(0);
    const mutationGenerationRef = useRef(0);

    // `t` wird im Lade-Effekt nur fuer Fehlermeldungen gebraucht und ist bewusst
    // KEINE Dependency: `src/i18n/config.ts` bindet react-i18next mit
    // `bindI18n: 'languageChanged loaded'` und `bindI18nStore: 'added removed'`
    // an 27 einzeln ueber HTTP nachgeladene Namespaces. Jedes eintreffende
    // Bundle liefert eine neue `t`-Referenz und haette so einen weiteren
    // vollstaendigen Firestore-Read plus Lade-Spinner ausgeloest. Ueber das Ref
    // bleibt die Fehlermeldung trotzdem in der aktuell aktiven Sprache.
    const tRef = useRef(t);
    tRef.current = t;

    // URL-Parameter auslesen
    const location = useLocation();
    const navigate = useNavigate();
    const queryParams = new URLSearchParams(location.search);
    const offerIdFromUrl = queryParams.get('id');

    useEffect(() => {
        const myGen = ++loadGenerationRef.current;
        const myMutationGen = mutationGenerationRef.current;
        /** Eine neuere Anfrage laeuft — deren Antwort gewinnt. */
        const isSuperseded = () => myGen !== loadGenerationRef.current;
        /** Seit dem Anfragestart wurde lokal mutiert — dieser Snapshot ist veraltet. */
        const isPreMutation = () => myMutationGen !== mutationGenerationRef.current;

        const fetchOffers = async () => {
            if (!currentUser) {
                setError(tRef.current('archive.errors.userNotLoggedIn'));
                setLoading(false);
                return;
            }

            setLoading(true);
            setError(null);

            try {
                console.log(`Fetching offers for user: ${effectiveScopeUid}`);

                // Wenn eine spezifische Angebots-ID angegeben wurde, hole nur dieses Angebot
                if (offerIdFromUrl) {
                    console.log(`Suche nach spezifischem Angebot mit ID: ${offerIdFromUrl}`);
                    try {
                        const offerDocRef = doc(db, 'offers', offerIdFromUrl);
                        const offerDoc = await getDoc(offerDocRef);

                        if (offerDoc.exists() && offerDoc.data().userId === effectiveScopeUid) {
                            if (isSuperseded() || isPreMutation()) return;
                            const data = offerDoc.data() as Omit<Offer, 'id'>;
                            setOffers([{ id: offerDoc.id, ...data }]);
                            setFilteredOffers([{ id: offerDoc.id, ...data }]);
                            setIsFiltered(true);
                            console.log('Spezifisches Angebot gefunden:', offerDoc.id);
                        } else {
                            console.log('Spezifisches Angebot nicht gefunden oder Zugriff verweigert');
                            // Trotzdem alle Angebote laden, falls das spezifische nicht gefunden wurde
                            await fetchAllOffers();
                        }
                    } catch (err) {
                        console.error('Fehler beim Laden des spezifischen Angebots:', err);
                        // Trotzdem alle Angebote laden
                        await fetchAllOffers();
                    }
                } else {
                    // Alle Angebote laden
                    await fetchAllOffers();
                }
            } catch (err) {
                if (isSuperseded()) return;
                console.error("Error fetching offers: ", err);
                setError(tRef.current('archive.errors.loadingError'));
                if (err instanceof Error) {
                     console.error(err.message);
                     console.error(err.stack);
                }
            } finally {
                // Nur die juengste Anfrage besitzt den Lade-Zustand. Eine bloss
                // durch eine lokale Mutation entwertete Antwort ist die
                // juengste und muss `loading` aufloesen.
                if (!isSuperseded()) setLoading(false);
            }
        };

        // Funktion zum Laden aller Angebote
        const fetchAllOffers = async () => {
            const offersRef = collection(db, 'offers');
            const q = query(
                offersRef,
                where('userId', '==', effectiveScopeUid),
                orderBy('createdAt', 'desc') // Neueste zuerst
            );

            const querySnapshot = await getDocs(q);
            const fetchedOffers: Offer[] = [];
            querySnapshot.forEach((doc) => {
                const data = doc.data() as Omit<Offer, 'id'>;
                const rawData = (doc.data() as any).rawData;
                const buyerEmail = rawData?.buyer?.email || undefined;
                const emailStatus = (doc.data() as any).emailStatus || undefined;
                fetchedOffers.push({ id: doc.id, ...data, buyerEmail, emailStatus });
            });

            if (isSuperseded() || isPreMutation()) return;
            setOffers(fetchedOffers);
            setFilteredOffers(fetchedOffers);
            console.log('Successfully fetched offers:', fetchedOffers.length);
        };

        fetchOffers();
    }, [currentUser, effectiveScopeUid, offerIdFromUrl]);

    // Suchfunktion
    const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
        const term = e.target.value.toLowerCase();
        setSearchTerm(term);

        if (term.trim() === '') {
            setFilteredOffers(offers);
            setIsFiltered(false);

            // URL-Parameter entfernen, wenn Suche geleert wird
            if (offerIdFromUrl) {
                navigate('/dashboard/angebote-archiv');
            }

            return;
        }

        // Nach Angebotsnummer, Kundennamen und Datum filtern
        const filtered = offers.filter(offer =>
            (offer.offerNumber && offer.offerNumber.toLowerCase().includes(term)) ||
            (offer.buyerName && offer.buyerName.toLowerCase().includes(term)) ||
            (offer.id && offer.id.toLowerCase().includes(term))
        );

        setFilteredOffers(filtered);
        setIsFiltered(true);
    };

    // Filter zurücksetzen
    const handleResetFilter = () => {
        setSearchTerm('');
        setFilteredOffers(offers);
        setIsFiltered(false);

        // URL-Parameter entfernen
        if (offerIdFromUrl) {
            navigate('/dashboard/angebote-archiv');
        }
    };

    const handleSendEmail = async (offer: Offer) => {
        if (!emailInput.trim()) return;
        setEmailSendStatus(prev => ({ ...prev, [offer.id]: 'sending' }));
        try {
            const resend = httpsCallable(functions, 'resendDocumentEmail');
            await resend({ docType: 'offer', docId: offer.id, recipientEmail: emailInput.trim() });
            setEmailSendStatus(prev => ({ ...prev, [offer.id]: 'success' }));
            // Optimistisches Update: emailStatus sofort im lokalen State setzen
            const sentEmail = emailInput.trim();
            mutationGenerationRef.current += 1;
            setOffers(prev => prev.map(o => o.id === offer.id
                ? { ...o, emailStatus: { status: 'sent' as const, recipientEmail: sentEmail } }
                : o
            ));
            setFilteredOffers(prev => prev.map(o => o.id === offer.id
                ? { ...o, emailStatus: { status: 'sent' as const, recipientEmail: sentEmail } }
                : o
            ));
            setTimeout(() => {
                setSendEmailFor(null);
                setEmailInput('');
                setEmailSendStatus(prev => { const s = { ...prev }; delete s[offer.id]; return s; });
            }, 2500);
        } catch {
            setEmailSendStatus(prev => ({ ...prev, [offer.id]: 'error' }));
        }
    };

    const handleDownload = async (offer: Offer) => {
        if (!offer.pdfPath) {
            setError(t('archive.errors.pathNotFound'));
            return;
        }
        setDownloading(offer.id);
        setError(null);

        try {
            // Advisor-Kontext (cross-tenant read-only): geladenes Angebot gehört einem fremden
            // Scope → Storage-Direktpfad scheitert. Tenant-aware Callable + Blob-Download.
            // Owner-Zweig unten (getDownloadURL + window.open) bleibt unverändert.
            if (effectiveScopeUid !== currentUser?.uid) {
                const file = await downloadTenantDocumentFile('offer', offer.id, 'pdf');
                triggerTenantFileDownload(file, `${offer.offerNumber || offer.id}.pdf`);
                return;
            }

            console.log(`Attempting to get download URL for path: ${offer.pdfPath}`);
            const pdfRef = ref(storage, offer.pdfPath);
            const url = await getDownloadURL(pdfRef);
            console.log(`Successfully got download URL: ${url}`);

            // Öffnen in neuem Tab
            window.open(url, '_blank');
        } catch (err) {
             console.error("Error getting download URL or downloading file: ", err);
             if (err instanceof Error && 'code' in err) {
                 switch ((err as any).code) {
                    case 'storage/object-not-found':
                        setError(t('archive.errors.fileNotFound', { path: offer.pdfPath }));
                        break;
                    case 'storage/unauthorized':
                    case 'functions/permission-denied':
                        setError(t('archive.errors.unauthorized'));
                        break;
                    case 'functions/resource-exhausted':
                        setError(t('archive.errors.downloadError'));
                        break;
                    default:
                        setError(t('archive.errors.downloadError'));
                 }
             } else {
                setError(t('archive.errors.unknownError'));
             }
             if (err instanceof Error) {
                 console.error(err.message);
                 console.error(err.stack);
            }
        } finally {
             setDownloading(null);
        }
    };

    // Helper zum Formatieren des Status
    const formatStatus = (status: string | undefined): string => {
        if (!status) return t('status.created');

        const statusMap: Record<string, string> = {
            'created': t('status.created'),
            'sent': t('status.sent'),
            'accepted': t('status.accepted'),
            'declined': t('status.declined'),
            'rejected': t('status.rejected'),
            'expired': t('status.expired'),
            'revised': t('status.revised'),
            'invoiced': t('status.invoiced')
        };

        return statusMap[status] || status;
    };

    // Zuerst füge ich eine Hilfsfunktion zur Prüfung des Status ein, nach der formatStatus-Funktion
    const canCreateInvoice = (status?: string): boolean => {
        // Nur für Angebote mit Status "accepted" (akzeptiert) eine Rechnung erstellen
        return status?.toLowerCase() === 'accepted';
    };

    /** E-Mail-Versand-Formular öffnen/schließen (unverändertes Verhalten, nur ausgelagert). */
    const toggleEmailForm = (offer: Offer) => {
        if (sendEmailFor === offer.id) {
            setSendEmailFor(null);
        } else {
            setSendEmailFor(offer.id);
            setEmailInput(offer.buyerEmail || '');
            setEmailSendStatus(prev => { const s = { ...prev }; delete s[offer.id]; return s; });
        }
    };

    // Menü-Items der Aktions-Spalte (M-V4-B, architecture-ux-v4.md §2.2) —
    // Reihenfolge: Download (immer) → Rechnung erstellen (Link-Item, nur
    // akzeptierte Angebote) → E-Mail (Versandpfad + !isReadOnly). Gating 1:1
    // aus dem Bestand.
    const buildOfferActionItems = (offer: Offer): RowActionsMenuItem[] => {
        const items: RowActionsMenuItem[] = [
            {
                id: 'download',
                label: t('archive.table.tooltips.downloadPdf', 'PDF öffnen'),
                icon: IconPdf,
                variant: 'accent',
                loading: downloading === offer.id,
                onSelect: () => handleDownload(offer),
            },
        ];
        // Rechnung erstellen — M11b-3: für advisor (read-only) ausgeblendet.
        // Bleibt ein Link-Item (Router-Ziel; erhält Middle-Click/Neuer-Tab —
        // RowActionsMenu rendert `to`-Items als <Link role="menuitem">).
        if (!isReadOnly && canCreateInvoice(offer.status)) {
            items.push({
                id: 'createInvoice',
                label: t('archive.table.tooltips.createInvoice'),
                icon: IconCreateInvoice,
                // Root Cause wie beim Storno-Button/offerId-Link (Support-Ticket Irene
                // Bayer, 2026-08-01; Reviewer-Finding F-1, Re-Loop 1): das hartkodierte
                // `/dashboard`-Literal navigierte seit der GA-Freischaltung des
                // Owner-Cockpits (REACT_APP_UX_COCKPIT_ENABLED=true, 2026-07-26) ins
                // Leere, weil `/dashboard` dort `CockpitPage` statt `InvoiceDashboard`
                // rendert. Fix: IMMER `INVOICE_FORM_ROUTE` verwenden (echte, flag-freie
                // Konstante aus `architecture-cockpit-route-split.md` §3 E2).
                // Zweiter Teil derselben Bug-Klasse (Folge-Ticket zu Review-Report F-1):
                // der Param hieß hier `fromOffer` und hatte NIRGENDS einen Konsumenten —
                // `InvoiceDashboard` liest ausschließlich `offerId` (`InvoiceDashboard.tsx:276`
                // → `handleOfferSelection`, das Käuferdaten und Positionen des Angebots ins
                // Formular übernimmt). Dieses Menü-Item war damit schon vor dem Cockpit-Flip
                // funktionslos: der Klick öffnete das LEERE Rechnungsformular, das Angebot
                // wurde nie übernommen. Fix: `offerId` — derselbe Param, den der bereits
                // produktive Angebot→Rechnung-Link in `WorkflowDetailPage.tsx` benutzt.
                to: `${INVOICE_FORM_ROUTE}?offerId=${offer.id}`,
            });
        }
        // Email — M11b-3: für advisor (read-only) ausgeblendet (Versand = mutierend).
        if (!isReadOnly && offer.pdfPath) {
            items.push({
                id: 'email',
                label: t('common:emailSend.resendButton'),
                icon: IconEmail,
                onSelect: () => toggleEmailForm(offer),
            });
        }
        return items;
    };

    // Aktions-Spalte je Zeile (D-V4-1, architecture-ux-v4.md §2.2): EmailStatusChip
    // bleibt SICHTBAR (Status-Anzeige, keine Aktion), Download/Rechnung-erstellen/
    // E-Mail wandern gemeinsam ins RowActionsMenu. Gating (isReadOnly / pdfPath)
    // 1:1 aus dem Bestand.
    const renderActions = (offer: Offer) => (
        <div className="flex items-center gap-1 justify-end">
            {/* emailStatus-Minitext → EmailStatusChip (§5.7/§9); undefined ⇒ null.
                Sichtbarkeits-Gate 1:1 wie zuvor (Parität, Bestand war Kind
                desselben !isReadOnly-Blocks). */}
            {!isReadOnly && offer.pdfPath && (
                <EmailStatusChip emailStatus={offer.emailStatus} locale={i18n.language} />
            )}
            <RowActionsMenu items={buildOfferActionItems(offer)} />
        </div>
    );

    // E-Mail-Versand-Sub-Row: NUR für die Zeile mit offenem Formular
    // (sendEmailFor === offer.id). Inhalt 1:1 aus dem Bestand.
    const renderRowDetail = (offer: Offer) => {
        if (sendEmailFor !== offer.id) return null;
        return (
            <div className="flex items-center gap-3 flex-wrap">
                <span className="text-sm text-muted shrink-0">{t('common:emailSend.emailLabel')}:</span>
                <input
                    type="email"
                    value={emailInput}
                    onChange={e => setEmailInput(e.target.value)}
                    placeholder={t('common:emailSend.resendInputPlaceholder')}
                    className="px-3 py-1.5 text-sm bg-surface-inset border border-strong rounded text-primary focus:outline-none focus:border-indigo-400 w-64"
                    disabled={emailSendStatus[offer.id] === 'sending'}
                    onKeyDown={e => e.key === 'Enter' && handleSendEmail(offer)}
                    autoFocus
                />
                <button
                    onClick={() => handleSendEmail(offer)}
                    disabled={!emailInput.trim() || emailSendStatus[offer.id] === 'sending'}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-indigo-600 hover:bg-indigo-500 text-[#0C1322] rounded disabled:opacity-50 transition-colors"
                >
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"/></svg>
                    {emailSendStatus[offer.id] === 'sending' ? t('common:emailSend.resendSending') : t('common:emailSend.resendButton')}
                </button>
                <button onClick={() => setSendEmailFor(null)} className="text-sm text-muted hover:text-secondary transition-colors">
                    {t('cancel', { ns: 'common', defaultValue: 'Abbrechen' })}
                </button>
                {emailSendStatus[offer.id] === 'success' && <span className="text-[var(--tone-success-fg)] text-sm">{t('common:emailSend.resendSuccess')}</span>}
                {emailSendStatus[offer.id] === 'error' && <span className="text-[var(--tone-danger-fg)] text-sm">{t('common:emailSend.resendError')}</span>}
            </div>
        );
    };

    // Spalten-Definition (§5.5): Datum/Gültig hideBelow, Kunde truncate,
    // Aktionen sticky right, density="compact" am DataTable.
    const columns: Column<Offer>[] = [
        {
            id: 'offerNumber',
            header: t('archive.table.headers.offerNumber'),
            accessor: (offer) => (
                <span className="font-medium text-primary">
                    {offer.offerNumber || t('archive.table.values.unknown')}
                </span>
            ),
            sortValue: (offer) => offer.offerNumber ?? '',
        },
        {
            id: 'date',
            header: t('archive.table.headers.date'),
            hideBelow: 'md',
            accessor: (offer) => formatDate(offer.offerDate || offer.createdAt, { locale: i18n.language }),
            sortValue: (offer) => toSortMillis(offer.offerDate || offer.createdAt),
        },
        {
            id: 'customer',
            header: t('archive.table.headers.customer'),
            truncate: 'max-w-[14rem]',
            accessor: (offer) => offer.buyerName || t('archive.table.values.unknown'),
            sortValue: (offer) => offer.buyerName ?? '',
        },
        {
            id: 'status',
            header: t('archive.table.headers.status'),
            accessor: (offer) => formatStatus(offer.status),
            sortValue: (offer) => offer.status ?? '',
        },
        {
            id: 'validUntil',
            header: t('archive.table.headers.validUntil'),
            hideBelow: 'lg',
            accessor: (offer) => offer.validUntil ? formatDate(offer.validUntil, { locale: i18n.language }) : t('archive.table.values.unlimited'),
            sortValue: (offer) => offer.validUntil ? toSortMillis(offer.validUntil) : 0,
        },
        {
            id: 'totalAmount',
            header: t('archive.table.headers.totalAmount'),
            numeric: true,
            accessor: (offer) => offer.totalAmount
                ? `${Number(offer.totalAmount).toFixed(2)} ${offer.currency || 'EUR'}`
                : t('archive.table.values.unknown'),
            sortValue: (offer) => offer.totalAmount ?? 0,
        },
        {
            id: 'actions',
            header: t('archive.table.headers.actions'),
            sticky: 'right',
            accessor: (offer) => renderActions(offer),
        },
    ];

    return (
        <div>
            <div className="max-w-7xl mx-auto bg-surface-raised p-6 rounded-lg shadow-md">
                <h2 className="text-2xl font-bold text-center text-primary mb-6">{t('archive.title')}</h2>

                {/* Suchfeld */}
                <div className="mb-6">
                    <div className="flex flex-col md:flex-row gap-4">
                        <div className="flex-grow">
                            <input
                                type="text"
                                placeholder={t('archive.searchPlaceholder')}
                                value={searchTerm}
                                onChange={handleSearch}
                                className="w-full px-4 py-2 bg-surface-inset text-primary border border-strong rounded focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            />
                        </div>
                        {isFiltered && (
                            <button
                                onClick={handleResetFilter}
                                className="px-4 py-2 bg-surface-inset text-primary border border-strong rounded hover:bg-surface-hover transition-colors"
                            >
                                {t('archive.resetFilter')}
                            </button>
                        )}
                    </div>
                    {isFiltered && (
                        <p className="mt-2 text-sm text-muted">
                            {t('archive.filteredCount', { count: filteredOffers.length, total: offers.length })}
                            {offerIdFromUrl ? ` (${t('archive.filteredById', { id: offerIdFromUrl })})` : ''}
                        </p>
                    )}
                </div>

                {error && (
                    <div className="mb-4 p-4 bg-[var(--tone-danger-bg)] border border-[var(--tone-danger-border)] text-[var(--tone-danger-fg)] rounded" role="alert">
                        <strong className="font-bold">{t('archive.errors.error')}:</strong>
                        <span className="block sm:inline"> {error}</span>
                    </div>
                )}

                {loading ? (
                    <div className="flex justify-center items-center py-10">
                        <LoadingSpinner color="#ffffff" />
                    </div>
                ) : (
                    <>
                        {filteredOffers.length === 0 ? (
                            <div className="text-center py-10 text-secondary">
                                {isFiltered ? (
                                    <>
                                        <p>{t('archive.emptyState.noResults')}</p>
                                        <button
                                            onClick={handleResetFilter}
                                            className="mt-4 px-4 py-2 bg-indigo-600 text-[#0C1322] rounded hover:bg-indigo-700 transition-colors"
                                        >
                                            {t('archive.emptyState.showAll')}
                                        </button>
                                    </>
                                ) : (
                                    <div className="py-6">
                                        <IconOffer size={48} className="text-gray-600 mx-auto mb-4" />
                                        <p className="text-secondary font-medium">{t('archive.emptyState.noOffers')}</p>
                                        <p className="mt-1 text-sm text-muted">{t('archive.emptyState.noOffersSubtext')}</p>
                                        {/* M11b-3: advisor (read-only) darf kein Angebot erstellen — CTA ausgeblendet
                                            (Seite ist ohnehin AdvisorHidden-gewrapped, defensiv wie sonst im File). */}
                                        {!isReadOnly && (
                                            <Button
                                                onClick={() => navigate('/dashboard/angebote')}
                                                className="mt-5"
                                            >
                                                {t('archive.emptyState.ctaCreateOffer')}
                                            </Button>
                                        )}
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div className="mt-6">
                                <DataTable
                                    columns={columns}
                                    rows={filteredOffers}
                                    rowKey={(offer) => offer.id}
                                    density="compact"
                                    empty={<span>{t('archive.emptyState.noResults')}</span>}
                                    renderRowDetail={renderRowDetail}
                                    rowClassName={(offer) => offer.id === offerIdFromUrl ? 'bg-[var(--tone-accent-bg)]' : undefined}
                                />
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

export default OfferArchive;
